import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMessage } from "../../../blocks/message/MessageContext";
import { Spinner } from "@fluentui/react-components";
import { relationContactFormFields } from "./RelationContactFormFields";
import { ENTITY_RELATION_CONTACT } from "../../../utils/Constants";
import Ows from "../../../utils/OwsHandler";
import Form from "../../form/Form";

const RelationContactForm = () => {
  const navigate = useNavigate();
  const { showMessage } = useMessage();
  const [loading, setLoading] = useState(false);
  const [statusList, setStatusList] = useState({});
  const [enableStatus, setEnableStatus] = useState(false);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const settingsData = await Ows.handleRequest("Otys.Services.SettingsService.get", [
          { bo_client: ["crm_enable_contact_person_status"] },
          null,
        ]);
        
        if (settingsData.error) {
          throw new Error(settingsData.error.message);
        }

        const enabledStatus = Boolean(settingsData.response?.bo_client?.crm_enable_contact_person_status);

        if (!isMounted) {
          return;
        }
        setEnableStatus(enabledStatus);

        if (enabledStatus) {
          const data = await Ows.handleRequest("Otys.Services.RelationContactService.getOptionLists", [
            ["Person.statusName"],
          ]);

          if (data.error) {
            throw new Error(data.error.message);
          }

          setStatusList(data.response["Person.statusName"] || {});
        }

      } catch (error) {
        console.error(error);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const createRelationContact = async (formData) => {
    try {
      const data = await Ows.handleRequest("Otys.Services.RelationContactService.add", [
        {
          relationId: formData.relation,
          Person: {
            firstName: "New contact",
          },
        },
      ]);

      if (data.error) {
        throw new Error(data.error.message);
      }

      return data.response;
    } catch (error) {
      console.error(error);
    }
  };

  const updateRelationContact = async (uid, formData) => {
    setLoading(true);
    try {
      const payload = {
        Person: {
          firstName: formData.firstName,
          infix: formData.infix,
          lastName: formData.lastName,
          emailPrimary: formData.email,
          ...(formData.phone && {
              PhoneNumbers: {
              1: {
                phoneNumber: formData.phone,
              },
            },
          }),
          ...(enableStatus && { statusId: formData.status }),
        },
        ...(!enableStatus && { isPassive: formData.isPassiveSelect === "passive" }),
      };
      
      const data = await Ows.handleRequest("Otys.Services.RelationContactService.update", [
        uid,
        payload,
      ]);

      if (data.error) {
        throw new Error(data.error.message);
      }

      return data.response;
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const deleteRelationContact = async (uid) => {
    try {
      const data = await Ows.handleRequest("Otys.Services.RelationContactService.delete", [uid]);

      if (data.error) {
        throw new Error(data.error.message);
      }

      return data.response;
    } catch (error) {
      console.error(error);
    }
  }

  const handleSubmit = async (formData) => {
    const uid = await createRelationContact(formData);

    if (!uid) {
      showMessage("error", "Failed to create relation contact.");
      return;
    }

    const relationContact = await updateRelationContact(uid, formData);

    if (!relationContact) {
      showMessage("error", "Failed to create relation contact.");
      await deleteRelationContact(uid);
      return;
    }
    
    showMessage("success", "Relation contact created successfully.");
    navigate(`/${ENTITY_RELATION_CONTACT.toLowerCase()}/${uid}`, { state: { skipPageHeader: true } });
  };

  return (
    <>
      {loading ? (
        <Spinner />
      ) : (
        <Form
          title="Create new relation contact"
          fields={relationContactFormFields(enableStatus, statusList)}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
};

export default RelationContactForm;
